import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useIsActiveSceneFlatImageTracked } from '@/utils';
import { useThree } from '@react-three/fiber';
import { useLayoutEffect, useEffect, useRef, useState, forwardRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Euler, Quaternion } from 'three';
import { useBillboardObject3d } from '../../r3f-components/components/CustomBillboard/hooks';
import { TransformControls as TransformControlsImpl } from './TransformControlsThree';
const pick = (haystack, keyNames) => {
    // @ts-expect-error needs refactor //TODO: fix this. at the moment it just returns the whole haystack
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return ({ keyNames } = haystack);
};
const omit = (obj, props) => {
    obj = { ...obj };
    props.forEach((prop) => delete obj[prop]);
    return obj;
};
const TransformControlsReactWrapper = forwardRef(({ startPosition, initialEntityRotation, locked, entityId, billboard, onChange, onMouseDown, onMouseUp, onObjectChange, object, ...props }, ref) => {
    const transformOnlyPropNames = ['enabled', 'axis', 'mode', 'translationSnap', 'rotationSnap', 'scaleSnap', 'space', 'size', 'showX', 'showY', 'showZ'];
    const { camera, ...rest } = props;
    const transformProps = pick(rest, transformOnlyPropNames);
    const objectProps = omit(rest, transformOnlyPropNames);
    // @ts-expect-error new in @react-three/fiber@7.0.5
    const defaultControls = useThree((state) => state.controls);
    const gl = useThree(({ gl }) => gl);
    const defaultCamera = useThree(({ camera }) => camera);
    const invalidate = useThree(({ invalidate }) => invalidate);
    const explCamera = camera || defaultCamera;
    const [controls] = useState(() => new TransformControlsImpl(explCamera, gl.domElement, { initialEntityRotation, locked }));
    const groupRef = useRef(null);
    const isFlatOrientation = useIsActiveSceneFlatImageTracked();
    const mode = useSelector((state) => state.userReducer.transformControlsMode);
    const billboardQuatRef = useRef(new Quaternion());
    const invBillboardQuatRef = useRef(new Quaternion());
    const correctedEulerRef = useRef(new Euler());
    const lockY = !billboard?.[1] ? true : false;
    useBillboardObject3d({ object3d: groupRef, rotation: initialEntityRotation, follow: !!billboard?.[0], lockY, billboardQuatRef, isCameraOrbiting: true });
    const onObjectRotationChange = useCallback((e) => {
        if (billboard?.[0]) {
            if (!groupRef.current)
                return;
            invBillboardQuatRef.current.copy(billboardQuatRef.current).invert();
            const correctedQuat = groupRef.current.quaternion.clone().premultiply(invBillboardQuatRef.current);
            onObjectChange?.(e, correctedEulerRef.current.setFromQuaternion(correctedQuat));
        }
        else {
            onObjectChange?.(e);
        }
    }, [billboard, groupRef, invBillboardQuatRef, billboardQuatRef, correctedEulerRef, onObjectChange, lockY]);
    useEffect(() => {
        controls.locked = !!locked;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locked]);
    // Without this, manipulating the position in transform panel will move the entity but not the gizmo
    useLayoutEffect(() => {
        if (!object && startPosition)
            groupRef.current?.position.set(startPosition[0], startPosition[1], startPosition[2]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startPosition]);
    useLayoutEffect(() => {
        groupRef.current?.rotation.set(initialEntityRotation[0], initialEntityRotation[1], initialEntityRotation[2]);
    }, [initialEntityRotation]);
    useLayoutEffect(() => {
        controls?.attach(groupRef.current);
        if (!object && startPosition) {
            groupRef.current?.position.set(startPosition[0], startPosition[1], startPosition[2]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId]);
    useEffect(() => {
        if (defaultControls) {
            const callback = (event) => {
                defaultControls.enabled = !event.value;
            };
            controls.addEventListener('dragging-changed', callback);
            return () => controls.removeEventListener('dragging-changed', callback);
        }
    }, [controls, defaultControls]);
    useEffect(() => {
        const callback = (e) => {
            invalidate();
            if (onChange)
                onChange(e);
        };
        controls?.addEventListener?.('change', callback);
        if (onMouseDown)
            controls?.addEventListener?.('mouseDown', onMouseDown);
        if (onMouseUp)
            controls?.addEventListener?.('mouseUp', onMouseUp);
        if (onObjectChange) {
            if (mode !== 'rotate')
                controls?.addEventListener?.('objectChange', onObjectChange);
            else
                controls?.addEventListener?.('objectChange', onObjectRotationChange);
        }
        return () => {
            controls?.removeEventListener?.('change', callback);
            if (onMouseDown)
                controls?.removeEventListener?.('mouseDown', onMouseDown);
            if (onMouseUp)
                controls?.removeEventListener?.('mouseUp', onMouseUp);
            if (onObjectChange)
                controls?.removeEventListener?.('objectChange', onObjectChange);
            if (onObjectChange)
                controls?.removeEventListener?.('objectChange', onObjectRotationChange);
        };
    }, [onChange, onMouseDown, onMouseUp, onObjectChange, controls, invalidate, mode, onObjectRotationChange]);
    // This is required in image tracked flat scenes to counteract the behaviour of the rotation of the "Editor Canvas Group" named parent component ( within EditorCanvas.tsx )
    useLayoutEffect(() => {
        if (isFlatOrientation) {
            controls.rotation.x = Math.PI / 2;
        }
        else {
            controls.rotation.x = 0;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFlatOrientation]);
    return controls ? (_jsxs("group", { children: [_jsx("primitive", { ref: ref, dispose: undefined, object: controls, ...transformProps }), _jsx("group", { ref: groupRef, children: _jsx("group", { ...objectProps }) })] })) : null;
});
TransformControlsReactWrapper.displayName = 'TransformControlsReactWrapper';
export default TransformControlsReactWrapper;
